import { useEffect, useRef, type JSX } from 'react'

import { useTranslation } from '../i18n.js'

/**
 * What a form got wrong, at the top, with a link to each field. `DESIGN-TOKENS.md` §16.5.
 *
 * > "inline validation on blur, never on keystroke · **form error summary at the top listing every
 * > failed field as a link**"
 *
 * **Nothing in the counter had one.** Three screens put `aria-invalid` on a field and the rest show
 * a single sentence under the submit button, which answers "did it work" and not "what do I fix".
 * On a form long enough to scroll, and the shop setup and the order form both are, a sentence at
 * the bottom about a field at the top is a message a shopkeeper has to go hunting for.
 *
 * **Links rather than text**, because that is the difference the requirement is asking for: the
 * summary is a way to get to the field, not a second place to read the same words. Each one moves
 * focus to the input, so a keyboard user lands where the typing has to happen, which is release
 * gate 1's stance carried past the Rx grid.
 *
 * **It takes focus when it appears and not after that.** A summary that steals focus on every
 * render fights whoever is typing; one that never takes it is a message to a screen reader that
 * arrives silently. The effect is keyed on the set of fields, so a new failure announces and a
 * re-render of the same failures does not.
 */

export interface FieldError {
  /** The `id` of the input this is about, so the link can reach it. */
  readonly fieldId: string
  /** What the field is called, as the link's text. */
  readonly label: string
  readonly message: string
}

export interface FormErrorsProps {
  readonly errors: readonly FieldError[]
}

export function FormErrors({ errors }: FormErrorsProps): JSX.Element | null {
  const { t } = useTranslation()
  const box = useRef<HTMLDivElement>(null)
  const key = errors.map((e) => e.fieldId).join('|')

  useEffect(() => {
    if (key !== '') box.current?.focus()
  }, [key])

  if (errors.length === 0) return null

  return (
    <div
      ref={box}
      className="lm-form-errors"
      role="alert"
      // Focusable but not in the tab order: it is reached by the failure, not by tabbing past it.
      tabIndex={-1}
    >
      <h2 className="lm-form-errors__heading">
        {t('formErrors.heading', { count: errors.length })}
      </h2>
      <ul className="lm-form-errors__list">
        {errors.map((error) => (
          <li key={error.fieldId}>
            <a
              className="lm-form-errors__link"
              href={`#${error.fieldId}`}
              onClick={(event) => {
                // The anchor alone scrolls; focus is what lets somebody start typing the fix.
                event.preventDefault()
                document.getElementById(error.fieldId)?.focus()
              }}
            >
              {error.label}
            </a>{' '}
            {/* The join is a catalogue string, not a colon written here: punctuation between a
                label and a sentence is not the same mark in every script. */}
            {t('formErrors.item', { message: error.message })}
          </li>
        ))}
      </ul>
    </div>
  )
}
